<template>
    <div class="emd-input-tag" :class="{ '_focus': isFocus, '_disabled': disabled }" @click="clicked">
    <ul>
      <template v-for="(item, i) in items">
        <template v-if="listType === 'complex'">
          <li :key="item.key" ref="key">
            <code>
              <emd-badge :text="`${item.key}:`" />
            </code>
          </li>
          <li v-for="(tag, j) in item.tags" :key="`${item.key}_${j}`">
            <emd-tag :text="tag" :color="badgeColor" :disabled="disabled" @remove="removeItem({ i: i, j: j })" />
          </li>
        </template>
        <li :key="i" v-else-if="listType === 'simple'">
          <emd-tag :text="item" :color="badgeColor" :disabled="disabled" @remove="removeItem(i)" />
        </li>
      </template>
    </ul>
    <input ref="tagInput" :type="input.type" :placeholder="input.placeholder" v-model="inputModel" v-on:blur="handleBlur" v-on:focus="handleFocus" :disabled="disabled" @keydown="onKeyDown" @paste="handlePasteEvent" />
  </div>
</template>

<script>
import EmdTag from '../../atoms/emdTag/EmdTag.vue'
import EmdBadge from '../../atoms/emdBadge/EmdBadge.vue'

export default {
  name: 'EmdInputTag',
  components: {
    EmdBadge,
    EmdTag
  },
  props: {
    list: {
      type: Array,
      default: () => [],
      required: false
    },
    listType: {
      type: String,
      default: 'simple',
      required: false,
      validator: (value) => {
        return ['simple', 'complex'].includes(value)
      }
    },
    disabled: {
      type: Boolean,
      default: false,
      required: false
    },
    focus: {
      type: Boolean,
      default: false,
      required: false
    },
    separatorCharacter: {
      type: String,
      default: ',',
      required: false,
      validator: (value) => {
        return [' ', ','].includes(value)
      }
    },
    validKeys: {
      type: Array,
      default: null,
      required: false
    },
    input: {
      type: Object,
      default: () => {
        return {
          type: 'text',
          placeholder: '',
          value: null
        }
      },
      required: false
    },
    badgeColor: {
      type: String,
      default: 'primary',
      required: false
    }
  },
  data () {
    return {
      isFocus: this.focus,
      inputModel: this.input && this.input.value ? this.input.value : ''
    }
  },
  computed: {
    items: {
      get () {
        return this.list
      },
      set () {}
    }
  },
  created () {
    if (this.listType === 'complex' && !this.validKeys) console.error('Missing required prop: "validKeys"') // validKeys required se listType for complex
  },
  watch: {
    list (value) {
      this.items = [].concat(value)
    },
    inputModel (value) {
      this.$emit('inputChanged', value)
    },
    input (input) {
      if (![null, undefined].includes(input.value)) {
        this.inputModel = input.value
      }
    },
    focus (value) {
      this.isFocus = value
      if (value) {
        this.$refs.tagInput.focus()
      } else {
        this.$refs.tagInput.blur()
      }
    }
  },
  methods: {
    clicked (event) {
      if (this.$refs.tagInput) {
        this.$refs.tagInput.focus()
      }
      this.$emit('onClick')
    },
    handleFocus (event) {
      this.isFocus = true
      this.$emit('onFocus')
    },
    handleBlur (event) {
      this.isFocus = false
      this.$emit('onBlur')
    },
    onKeyDown (event) {
      this.$emit('keydown', this.inputModel)
      if (event.key === 'Enter') {
        event.preventDefault()
        this.handleSubmitEvent(event)
      } else if (event.key === 'Backspace' && [undefined, null, ''].includes(this.inputModel)) {
        this.handleBackspaceEvent(event)
      }
    },
    removeItem (index) {
      if (this.listType === 'simple') {
        this.items.splice(index, 1)
      } else if (this.listType === 'complex') {
        if (this.items[index.i].tags.length === 1) {
          this.items.splice(index.i, 1)
        } else {
          this.items[index.i].tags.splice(index.j, 1)
        }
      }
      this.$emit('listUpdated', this.items)
    },
    handleSubmitEvent () {
      if (this.isEmpty(this.inputModel)) {
        this.inputModel = ''
      } else {
        this.splitText(this.inputModel)
      }
      this.$emit('onSubmit', this.items)
    },
    handleBackspaceEvent (event) {
      if (this.items.length > 0) {
        const i = this.items.length - 1
        if (this.listType === 'simple') {
          this.inputModel = `${this.items.splice(i, 1)[0]} `
        } else if (this.listType === 'complex') {
          const totalTags = this.items[i].tags.length
          if (totalTags === 0) {
            this.inputModel = `${this.items[i].key} `
            this.items.splice(i, 1)
          } else {
            this.inputModel = `${this.items[i].tags.splice(totalTags - 1, 1)[0]} `
          }
        }
        this.$emit('listUpdated', this.items)
      }
    },
    handlePasteEvent (event) {
      const text = event.clipboardData.getData('text')
      const hasLineBreaks = /\r|\n|[^\S ]/.exec(text) // has new line (excel row) or tab (excel column)
      const hasSeparator = text.indexOf(this.separatorCharacter) > -1
      const hasKey = (this.listType === 'complex' && text.indexOf(':') > -1)
      if (hasLineBreaks || hasSeparator || hasKey) {
        event.preventDefault()
        this.splitText(text)
      }
    },
    splitText (text) {
      const c = this.separatorCharacter
      const notTab = '[^\\S ]'
      const separator = ` ${c} |${c} | ${c}|${c}`
      var listUpdated = false
      var newText = ''
      if (this.listType === 'simple') {
        const separatorRegex = RegExp(`(?:${separator}|${notTab})+`)
        const tags = text.split(separatorRegex).filter(e => e.trim())
        this.items.push(...tags)
        if (tags.length > 0) {
          listUpdated = true
        }
      } else if (this.listType === 'complex') {
        const sectionSeparator = ' ; | ;|; |;'
        const sectionRegex = RegExp(`(?:${sectionSeparator}|${notTab})+`)
        const keySeparator = ' : | :|: |:'
        const keySeparatorRegex = RegExp(`(?:${keySeparator}|${notTab})+`)
        const separatorRegex = RegExp(`(?:${separator}|${notTab})+`)
        const sections = text.split(sectionRegex).filter(e => e)
        sections.forEach(section => {
          const keyValue = section.split(keySeparatorRegex)
          const index = this.validKeys.findIndex(element => element.keys.includes(keyValue[0]))
          const itemsCount = this.items.length
          if (keyValue.length === 2) { // existe chave e valor
            if (index > -1) { // a chave é uma chave válida
              const indexInList = this.items.findIndex(element => this.validKeys[index].query === element.type)
              const tags = keyValue[1] ? keyValue[1].split(separatorRegex).filter(e => e.trim()) : []
              if (indexInList < 0) { // a chave não existe na listagem
                if (itemsCount === 0) {
                  this.items.push({
                    type: this.validKeys[index].query,
                    key: `${keyValue[0]}`,
                    tags: [].concat(tags)
                  })
                } else {
                  if (this.items[itemsCount - 1].tags.length > 0) {
                    this.items.push({
                      type: this.validKeys[index].query,
                      key: `${keyValue[0]}`,
                      tags: [].concat(tags)
                    })
                  } else {
                    if (tags.length > 0) {
                      this.items.splice(itemsCount - 1, 0, {
                        type: this.validKeys[index].query,
                        key: `${keyValue[0]}`,
                        tags: [].concat(tags)
                      })
                    } else {
                      newText += `${section} `
                    }
                  }
                }
              } else {
                this.$emit('repeatedKey', keyValue[0])
                this.items[indexInList].tags.push(...tags)
              }
              if (tags.length > 0) {
                listUpdated = true
              }
            } else {
              newText += `${section}; `
              this.$emit('invalidKey', keyValue[0])
            }
          } else if (keyValue.length === 1) {
            const tags = keyValue[0].split(separatorRegex).filter(e => e.trim())
            if (tags.length > 0) {
              if (itemsCount === 0) {
                if (this.validKeys[0]) {
                  this.items.push({
                    type: this.validKeys[0].query,
                    key: `${this.validKeys[0].keys[0]}`,
                    tags: [].concat(tags)
                  })
                  listUpdated = true
                }
              } else {
                this.items[itemsCount - 1].tags.push(...tags)
                listUpdated = true
              }
            }
          } else {
            newText += `${section} `
          }
        })
      }
      this.inputModel = newText
      if (listUpdated) {
        this.$emit('listUpdated', this.items)
      }
    },
    isEmpty (value) {
      return (/^\s*$/).test(value) || [undefined, null, ''].includes(value) // testa se é espaço vazio
    }
  }
}
</script>
<style>
.emd-input-tag ul li .emd-badge {
    display: block;
    margin-top: -2px;
}
</style>
